import React from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Printer } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';

const PrivacyPolicy = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const handleClose = () => {
    // Geri git ve scroll to top sinyali gönder
    navigate(-1, { state: { scrollToTop: true } });
    // Ekstra güvenlik - biraz bekle ve scroll yap
    setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }, 50);
  };

  const handlePrint = () => {
    const printContent = document.getElementById('privacy-content');
    
    // Gizli iframe oluştur
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.top = '-10000px';
    iframe.style.left = '-10000px';
    document.body.appendChild(iframe);
    
    const iframeDoc = iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>SLÁI Events - ${t.privacy.title}</title>
          <style>
            @page { margin: 20mm; }
            body { font-family: Arial, sans-serif; padding: 10px; line-height: 1.5; font-size: 12pt; color: #333; }
            h1 { font-size: 24pt; margin-bottom: 16px; }
            h2 { font-size: 14pt; margin-top: 18px; margin-bottom: 10px; color: #333; }
            p { margin-bottom: 10px; color: #555; }
            ul { margin-left: 20px; margin-bottom: 10px; }
            li { margin-bottom: 4px; color: #555; }
            a { color: #d97706; }
            .footer { margin-top: 20px; padding-top: 14px; border-top: 1px solid #ccc; font-size: 10pt; color: #666; }
          </style>
        </head>
        <body>${printContent.innerHTML}</body>
      </html>
    `);
    iframeDoc.close();
    
    // Print ve iframe temizle
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
    
    // Print dialog kapandıktan sonra iframe'i kaldır
    setTimeout(() => {
      document.body.removeChild(iframe);
    }, 1000);
  };

  return (
    <div className="min-h-screen pt-16 bg-[#1a1a1a]">
      {/* Header */}
      <section className="py-12 sm:py-20 bg-[#1a1a1a] border-b border-white/10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center relative">
            <button
              onClick={handleClose}
              className="absolute top-0 right-0 p-2 text-white/60 hover:text-[#8b7355] transition-colors"
              aria-label="Close"
            >
              <X className="w-8 h-8" />
            </button>
            <p className="text-xs tracking-[0.3em] uppercase text-[#8b7355] mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              RECHTLICHES
            </p>
            <h1 className="text-4xl sm:text-6xl md:text-7xl text-white mb-4 font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {t.privacy.title}
            </h1>
            <div className="w-16 h-[1px] bg-white/40 mx-auto" />
          </div>
        </div>
      </section>

      {/* Privacy Content */}
      <section className="py-12 sm:py-16 bg-[#1a1a1a]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto bg-[#2a2a2a] border border-[#e67e22] rounded-lg p-8 sm:p-12">
            
            <div id="privacy-content" className="space-y-8 text-white/70">
              {/* Introduction */}
              <div>
                <h2 className="text-2xl text-white mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>{t.privacy.sections.intro.title}</h2>
                <p className="leading-relaxed" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>{t.privacy.sections.intro.content}</p>
              </div>

              {/* Data Collection */}
              <div>
                <h2 className="text-2xl text-white mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>{t.privacy.sections.dataCollection.title}</h2>
                <p className="leading-relaxed mb-4" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>{t.privacy.sections.dataCollection.content}</p>
                <ul className="list-disc list-inside space-y-2 ml-4" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
                  {t.privacy.sections.dataCollection.items.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>

              {/* Data Usage */}
              <div>
                <h2 className="text-2xl text-white mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>{t.privacy.sections.dataUsage.title}</h2>
                <p className="leading-relaxed mb-4" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>{t.privacy.sections.dataUsage.content}</p>
                <ul className="list-disc list-inside space-y-2 ml-4" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
                  {t.privacy.sections.dataUsage.items.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>

              {/* Data Protection */}
              <div>
                <h2 className="text-2xl text-white mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>{t.privacy.sections.dataProtection.title}</h2>
                <p className="leading-relaxed" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>{t.privacy.sections.dataProtection.content}</p>
              </div>

              {/* Cookies */}
              <div>
                <h2 className="text-2xl text-white mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>{t.privacy.sections.cookies.title}</h2>
                <p className="leading-relaxed" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>{t.privacy.sections.cookies.content}</p>
              </div>

              {/* Third Parties */}
              <div>
                <h2 className="text-2xl font-bold text-white mb-4">{t.privacy.sections.thirdParties.title}</h2>
                <p className="leading-relaxed">{t.privacy.sections.thirdParties.content}</p>
              </div>

              {/* Your Rights */}
              <div>
                <h2 className="text-2xl font-bold text-white mb-4">{t.privacy.sections.rights.title}</h2>
                <p className="leading-relaxed mb-4">{t.privacy.sections.rights.content}</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  {t.privacy.sections.rights.items.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>

              {/* Contact */}
              <div>
                <h2 className="text-2xl font-bold text-white mb-4">{t.privacy.sections.contact.title}</h2>
                <p className="leading-relaxed">{t.privacy.sections.contact.content}</p>
                <p className="mt-4 text-[#8b7355] font-semibold">info@slaievents.com</p>
              </div>

              {/* Last Updated */}
              <div className="pt-8 border-t border-white/30">
                <p className="text-sm text-gray-500">{t.privacy.lastUpdated}: December 2024</p>
              </div>
            </div>

            {/* Action Buttons - content dışında, yazdırılmayacak */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-12 pt-8 border-t border-white/20">
              <Button
                onClick={handlePrint}
                className="bg-[#3a3a3a] text-white border border-[#e67e22] hover:bg-[#4a4a4a] rounded-none uppercase tracking-[0.15em] text-xs px-8 py-3"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                <Printer className="w-4 h-4 mr-2" />
                DRUCKEN
              </Button>
              <Button
                onClick={handleClose}
                className="bg-[#3a3a3a] text-white border border-[#e67e22] hover:bg-[#4a4a4a] rounded-none uppercase tracking-[0.15em] text-xs px-8 py-3"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                <X className="w-4 h-4 mr-2" />
                SCHLIESSEN
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
